import { useState, useCallback } from 'react';
import FLASHCARDS from '../data/flashcardsData';
import '../styles/flashcards.css';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Flashcards({ onClose }) {
  const [deck, setDeck] = useState(FLASHCARDS);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);

  const card = deck[index];
  const total = deck.length;

  const goNext = useCallback(() => {
    if (index + 1 >= total) {
      setDone(true);
      return;
    }
    setFlipped(false);
    // small delay so the card resets before sliding in
    setTimeout(() => setIndex((i) => i + 1), 50);
  }, [index, total]);

  const goPrev = useCallback(() => {
    if (index === 0) return;
    setFlipped(false);
    setTimeout(() => setIndex((i) => i - 1), 50);
  }, [index]);

  const handleShuffle = useCallback(() => {
    setDeck(shuffle(FLASHCARDS));
    setIndex(0);
    setFlipped(false);
    setDone(false);
  }, []);

  const handleRestart = useCallback(() => {
    setDeck(FLASHCARDS);
    setIndex(0);
    setFlipped(false);
    setDone(false);
  }, []);

  const progressPct = ((index + (done ? 1 : 0)) / total) * 100;

  return (
    <div
      className="fc-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Flashcards"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="fc-modal">
        {/* Header */}
        <div className="fc-header">
          <div className="fc-title-row">
            <span className="fc-icon" aria-hidden="true">🧠</span>
            <h2 className="fc-title">Flashcards</h2>
          </div>
          <button className="fc-close" onClick={onClose} aria-label="Close flashcards">✕</button>
        </div>

        {/* Progress */}
        <div className="fc-progress-row">
          <div className="fc-progress-bar" aria-hidden="true">
            <div className="fc-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="fc-progress-label">
            {done ? total : index + 1} / {total}
          </span>
        </div>

        {done ? (
          /* ── Complete screen ───────────────────────────────────────── */
          <div className="fc-complete">
            <div className="fc-complete-emoji">🎉</div>
            <h3 className="fc-complete-heading">Deck complete!</h3>
            <p className="fc-complete-sub">
              You reviewed all {total} cards. Keep it up!
            </p>
            <button className="fc-restart-btn" onClick={handleRestart}>
              Start over
            </button>
            <button className="fc-shuffle-btn" onClick={handleShuffle}>
              🔀 Shuffle &amp; restart
            </button>
          </div>
        ) : (
          <>
            {/* ── Flip card ─────────────────────────────────────────── */}
            <div
              className="fc-card-wrap"
              onClick={() => setFlipped((f) => !f)}
              role="button"
              tabIndex={0}
              aria-label={flipped ? 'Show term' : 'Reveal definition'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setFlipped((f) => !f);
              }}
            >
              <div className={`fc-card${flipped ? ' fc-card--flipped' : ''}`}>
                {/* Front */}
                <div className="fc-face fc-face--front">
                  <span className={`fc-subject-badge fc-subject-badge--${card.subject}`}>
                    {card.subject}
                  </span>
                  <p className="fc-term">{card.term}</p>
                  <p className="fc-flip-hint">Tap to reveal definition</p>
                </div>

                {/* Back */}
                <div className="fc-face fc-face--back">
                  <div className="fc-back-badge-row">
                    <span className={`fc-subject-badge fc-subject-badge--${card.subject}`}>
                      {card.subject}
                    </span>
                    <span className="fc-back-label">{card.term}</span>
                  </div>
                  <p className="fc-definition">{card.definition}</p>
                  <p className="fc-fun-fact">
                    <span className="fc-fun-fact-label">Fun fact: </span>
                    {card.funFact}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="fc-nav">
              <button
                className="fc-nav-btn"
                onClick={goPrev}
                disabled={index === 0}
                aria-label="Previous card"
              >
                ← Prev
              </button>

              <button
                className="fc-flip-btn"
                onClick={() => setFlipped((f) => !f)}
                aria-label={flipped ? 'Show term' : 'Reveal definition'}
              >
                {flipped ? 'Show term' : 'Reveal'}
              </button>

              <button
                className="fc-nav-btn"
                onClick={goNext}
                aria-label={index + 1 < total ? 'Next card' : 'Finish deck'}
              >
                {index + 1 < total ? 'Next →' : 'Finish'}
              </button>
            </div>

            {/* Shuffle */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button className="fc-shuffle-btn" onClick={handleShuffle} aria-label="Shuffle deck">
                🔀 Shuffle deck
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
